<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'

const props = withDefaults(defineProps<{
  suggestions?: string[]
  inputElement: HTMLInputElement | null
  isVisible: boolean
}>(), {
  suggestions: () => []
})

const emit = defineEmits<{
  (e: 'select', suggestion: string): void
  (e: 'outside-click'): void
}>()

const dropdownRef = ref<HTMLDivElement | null>(null)

// Process suggestions to remove markdown formatting and quotes
const processedSuggestions = computed(() => {
  if (!props.suggestions) return []
  
  return props.suggestions.map(suggestion => {
    // Remove markdown bold formatting (** **) and double quotes but preserve text
    return suggestion.replace(/\*\*/g, '').replace(/"/g, '')
  })
})

// Handle click outside dropdown
const handleClickOutside = (event: MouseEvent) => {
  if (!props.isVisible) return
  
  // Check if the click was outside the dropdown and not on the input element
  if (
    dropdownRef.value && 
    !dropdownRef.value.contains(event.target as Node) &&
    props.inputElement !== event.target
  ) {
    emit('outside-click')
  }
}

// Position the dropdown based on the input element
const updatePosition = () => {
  if (!dropdownRef.value || !props.inputElement) return

  // No need to position manually since we're using CSS positioning
  // This ensures the dropdown is properly positioned relative to its parent
}

// Update position when window is resized
const onResize = () => {
  updatePosition()
}

onMounted(() => {
  window.addEventListener('resize', onResize)
  window.addEventListener('mousedown', handleClickOutside)
  updatePosition()
})

onUnmounted(() => {
  window.removeEventListener('resize', onResize)
  window.removeEventListener('mousedown', handleClickOutside)
})

// Select a suggestion
const selectSuggestion = (index: number) => {
  const cleanedSuggestion = processedSuggestions.value[index]
  
  // Emit the cleaned suggestion instead of the original one
  emit('select', cleanedSuggestion)
}
</script>

<template>
  <div v-if="isVisible" ref="dropdownRef" class="suggestions-dropdown">
    <ul>
      <li v-for="(suggestion, index) in processedSuggestions" :key="index" @click="selectSuggestion(index)">
        {{ suggestion }}
      </li>
    </ul>
  </div>
</template>

<style scoped>
.suggestions-dropdown {
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  background-color: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  max-height: 300px;
  overflow-y: auto;
  margin-bottom: 8px;
}

ul {
  list-style-type: none;
  padding: 0;
  margin: 0;
}

li {
  padding: 10px 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: 14px;
  color: #333; /* Darker text color for better readability */
  font-weight: 500; /* Slightly bolder text */
}

li:hover {
  background-color: #f0f8ff;
}

li:not(:last-child) {
  border-bottom: 1px solid #f0f0f0;
}
</style>